import { FunctionComponent, SetStateAction, useEffect, useRef, useState } from 'react';
import TransportationTypeSelector from './UI/TransportationTypeSelector/TransportationTypeSelector';
import TransportationServiceSelector from './UI/TransportationServiceSelector/TransportationServiceSelector';
import styles from './TransportationComponent.module.scss';
import StaffSelector from '../StaffSelector/StaffSelector';
import { useApiClientWrapper, useAuth, useMarketplace, useTimeZone } from 'hooks';
import { fetchTransportationServicesThunk } from 'actions/services/ServicesActions';
import { useDispatch, useSelector } from 'react-redux';
import { ServicesStatus } from 'reducers/services/ServicesState';
import TransportationServiceVehicleSelector from './UI/TransportationServiceVehicleSelector/TransportationServiceVehicleSelector';
import { TransportationSection } from './UI/TransportationSection/TransportationSection';
import { Transportation } from 'model/NewMultipleBooking';
import { RootState } from 'store';
import Button from 'components/UI/V3/Button/Button';
import { Moment } from 'moment-timezone';
import { fetchVehiclesForServiceIdThunk, VehiclesStatus } from '@spike/vehicles-action';
import { NewBookingsStatus } from '@spike/new-bookings-action';
import { RecentlyBooked } from '../UI/RecentlyBooked';
import { AppointmentHistoryDrawer } from '../UI/AppointmentHistoryDrawer';
import OrderDetail from 'components/OrderDetail';
import { AppointmentListItem } from '@spike/appointments-list-model';
import { showWarningThunk } from '@spike/notifications-action';
import {
    getDefaultTimeRange,
    getDefaultTimeRangeFromOpening,
    getDefaultTimeRangesForRoundtrip,
    getDefaultTimeRangesForRoundtripFromOpening,
    applyPickupTimeChange as applyPickupTimeChangeUtil,
    normalizeTimeRange
} from 'utils/timeRangeUtils';
import Client from '@spike/client-model';
import Staff from 'model/Staff';
import { Vehicle } from '@spike/vehicle-model';
import type { EnrollmentAnchor } from '../utils/enrollmentAnchor';
import type { Enrollment, MembershipItem } from '@spike/membership-model';
import { PetCustomPrice } from '@spike/pet-model';
import { fetchPetCustomPricesThunk } from '@spike/pets-action';
import { resolveDropoffPrice, resolvePickupPrice } from '../utils/bookingCustomPrices';
import MultipleDateSelector from '../DateSelector/MultipleDateSelector';
import { noop } from 'lodash';
import { mergeTransportationDayWithClock } from 'utils/transportationDateSlots';
import { computeMembershipPreviewDiscount } from '../utils/membershipDisplayPrice';
import type { ValueTypeSelectorValue } from 'components/UI/V3/Forms/ValueTypeSelector';

interface TransportationProps {
    clientId: number;
    petId: number;
    transportation: Transportation;
    edit: boolean;
    enrollment?: Enrollment;
    membershipItem?: MembershipItem;
    enrollmentAnchor?: EnrollmentAnchor;
    onNext: (transportation: Transportation) => void;
}

export const TransportationComponent: FunctionComponent<TransportationProps> = ({
    clientId,
    petId,
    transportation,
    edit,
    enrollment,
    membershipItem,
    enrollmentAnchor,
    onNext
}) => {
    const auth = useAuth();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const tz = marketplace.schedule.timeZone;
    const newBookingStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);
    const servicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);
    const vehiclesStatus = useSelector<RootState, VehiclesStatus>(state => state.vehicles.status);
    const vehicles = useSelector<RootState, Vehicle[] | undefined>(state => state.vehicles.vehicles);
    const staffs = useSelector<RootState, Staff[]>(state => state.staff.staff);
    const customPrices = useSelector<RootState, Array<PetCustomPrice>>(state => state.pets.customPrices);
    const timeZone = useTimeZone();
    const client = useSelector<RootState, Client | undefined>(state => state.clients.client);

    const getClientAddress = () => {
        if (!client) return '';
        return `${client?.address?.street ? `${client?.address?.street}, ` : ''}${
            client?.address?.city ? `${client?.address?.city}, ` : ''
        }${client?.address?.state ? `${client?.address?.state}, ` : ''}${
            client?.address?.zipcode ? `${client?.address?.zipcode}` : ''
        }`.trim();
    };

    const [transportationState, setTransportation] = useState<Transportation>(() => {
        const clientAddr = getClientAddress();
        if (clientAddr && transportation.transportType) {
            const needsPickup =
                transportation.transportType === 'pickup' || transportation.transportType === 'roundtrip';
            const needsDropOff =
                transportation.transportType === 'dropoff' || transportation.transportType === 'roundtrip';
            const pickupEmpty = !transportation.pickup?.address?.trim();
            const dropOffEmpty = !transportation.dropOff?.address?.trim();

            if ((needsPickup && pickupEmpty) || (needsDropOff && dropOffEmpty)) {
                return {
                    ...transportation,
                    pickup: {
                        ...transportation.pickup!,
                        address: needsPickup && pickupEmpty ? clientAddr : transportation.pickup?.address || ''
                    },
                    dropOff: {
                        ...transportation.dropOff!,
                        address: needsDropOff && dropOffEmpty ? clientAddr : transportation.dropOff?.address || ''
                    }
                };
            }
        }
        return transportation;
    });

    const [showAppointmentHistoryDrawer, setShowAppointmentHistoryDrawer] = useState(false);
    const [recentlyBookedRefreshKey, setRecentlyBookedRefreshKey] = useState(0);
    const [showOrderDetail, setShowOrderDetail] = useState<{ orderId: number; bookingId: number } | null>(null);
    const [selectedDates, setSelectedDates] = useState<Array<Moment>>(() => {
        if (transportation?.selectedDates?.length) {
            return transportation.selectedDates.map(d => d.clone().tz(tz).startOf('day'));
        }
        const anchor = transportation?.pickup?.time?.from || transportation?.dropOff?.time?.from;
        return anchor?.isValid() ? [anchor.clone().tz(tz).startOf('day')] : [];
    });

    const pickupPrice = transportationState?.pickup?.price ?? 0;
    const dropOffPrice = transportationState?.dropOff?.price ?? 0;
    const pickupAddress = transportationState?.pickup?.address ?? '';
    const dropOffAddress = transportationState?.dropOff?.address ?? '';

    const requiresPickup =
        transportationState?.transportType === 'pickup' || transportationState?.transportType === 'roundtrip';

    const requiresDropOff =
        transportationState?.transportType === 'dropoff' || transportationState?.transportType === 'roundtrip';

    const isDropOffEarlierThanPickup = (() => {
        if (transportationState?.transportType !== 'roundtrip') return false;
        const pickupTo = transportationState?.pickup?.time?.to;
        const dropOffFrom = transportationState?.dropOff?.time?.from;
        const dropOffTo = transportationState?.dropOff?.time?.to;
        if (!pickupTo || !dropOffFrom || !dropOffTo) return false;
        const pickupMax = pickupTo.clone().tz(timeZone);
        const from = dropOffFrom.clone().tz(timeZone);
        const to = dropOffTo.clone().tz(timeZone);
        return from.isBefore(pickupMax) || to.isBefore(pickupMax);
    })();

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setTransportation(transportation);
    }, [transportation]);

    const isDisabled =
        !transportationState?.transportType ||
        !transportationState?.service ||
        !transportationState?.selectedStaff ||
        !transportationState?.vehicle ||
        (requiresPickup && pickupPrice < 0) ||
        (requiresDropOff && dropOffPrice < 0) ||
        (requiresPickup && !pickupAddress) ||
        (requiresDropOff && !dropOffAddress) ||
        isDropOffEarlierThanPickup ||
        (!edit && selectedDates.length === 0);

    const multiDatePeriodFrom =
        transportationState?.pickup?.time?.from || transportationState?.dropOff?.time?.from || selectedDates[0];
    const multiDatePeriodTo =
        transportationState?.pickup?.time?.to ||
        transportationState?.dropOff?.time?.to ||
        multiDatePeriodFrom?.clone().add(1, 'hour');

    const changeSelectedDatesHandler = (datesOrUpdater: SetStateAction<Array<Moment>>) => {
        setSelectedDates(prev => {
            const next = typeof datesOrUpdater === 'function' ? datesOrUpdater(prev) : datesOrUpdater;
            return next.map(d => d.clone().tz(tz).startOf('day'));
        });
    };

    const changeMultiDatePeriodHandler = (from: Moment, to: Moment) => {
        if (requiresPickup && transportationState?.pickup) {
            setTransportation(prev => ({
                ...prev!,
                pickup: {
                    ...prev!.pickup!,
                    time: {
                        from: from.clone().tz(timeZone),
                        to: to.clone().tz(timeZone)
                    }
                }
            }));
        } else if (requiresDropOff && transportationState?.dropOff) {
            setTransportation(prev => ({
                ...prev!,
                dropOff: {
                    ...prev!.dropOff!,
                    time: {
                        from: from.clone().tz(timeZone),
                        to: to.clone().tz(timeZone)
                    }
                }
            }));
        }
    };

    useEffect(() => {
        dispatch(fetchTransportationServicesThunk(petId, apiClientWrapper.marketplaceId));
        dispatch(fetchPetCustomPricesThunk(apiClientWrapper, petId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petId, apiClientWrapper.marketplaceId]);

    useEffect(() => {
        if (transportationState?.service) {
            dispatch(fetchVehiclesForServiceIdThunk(apiClientWrapper, transportationState?.service?.serviceId));
        }
    }, [transportationState?.service]);

    /** StaffSelector shows auth user when selectedStaff is unset, but Save stayed disabled — sync real staff into state. */
    useEffect(() => {
        if (!transportationState?.service) return;
        if (transportationState.selectedStaff) return;
        const sid = auth.user?.staffId;
        if (sid == null || !staffs?.length) return;
        const staff = staffs.find(s => s.id === sid);
        if (!staff) return;
        setTransportation(prev => (prev ? { ...prev, selectedStaff: staff } : prev));
    }, [transportationState?.service, transportationState?.selectedStaff, auth.user?.staffId, staffs]);

    /** Preselect first vehicle when parent did not pass one (e.g. membership booking from pet). */
    useEffect(() => {
        if (!transportationState?.service) return;
        if (transportationState.vehicle) return;
        if (!vehicles?.length) return;
        setTransportation(prev => (prev && !prev.vehicle ? { ...prev, vehicle: vehicles[0] } : prev));
    }, [transportationState?.service?.serviceId, transportationState?.vehicle, vehicles]);

    useEffect(() => {
        if (client && transportationState?.transportType) {
            const clientAddress = getClientAddress();

            if (clientAddress !== '') {
                const requiresPickup =
                    transportationState.transportType === 'pickup' || transportationState.transportType === 'roundtrip';
                const requiresDropOff =
                    transportationState.transportType === 'dropoff' ||
                    transportationState.transportType === 'roundtrip';

                const hasExistingPickupAddress = !!(
                    transportationState?.pickup?.address && transportationState.pickup.address.trim() !== ''
                );
                const hasExistingDropOffAddress = !!(
                    transportationState?.dropOff?.address && transportationState.dropOff.address.trim() !== ''
                );

                const shouldUpdatePickup = requiresPickup && !hasExistingPickupAddress;
                const shouldUpdateDropOff = requiresDropOff && !hasExistingDropOffAddress;

                if (shouldUpdatePickup || shouldUpdateDropOff) {
                    setTransportation(prev => ({
                        ...prev!,
                        pickup: {
                            ...prev!.pickup!,
                            address: shouldUpdatePickup ? clientAddress : prev!.pickup!.address
                        },
                        dropOff: {
                            ...prev!.dropOff!,
                            address: shouldUpdateDropOff ? clientAddress : prev!.dropOff!.address
                        }
                    }));
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transportationState?.transportType]);

    const handlePickupTimeChange = (value: { from: Moment; to: Moment }) => {
        const { pickup, dropOff } = applyPickupTimeChangeUtil(value, transportationState?.dropOff?.time, timeZone);
        setTransportation({
            ...transportationState!,
            pickup: {
                ...transportationState!.pickup!,
                time: pickup
            },
            ...(dropOff && {
                dropOff: {
                    ...transportationState!.dropOff!,
                    time: dropOff
                }
            })
        });
    };

    const handleDropOffTimeChange = (value: { from: Moment; to: Moment }) => {
        const dropOffTime = normalizeTimeRange(value, timeZone);
        if (transportationState?.transportType === 'roundtrip' && transportationState?.pickup?.time?.to) {
            const pickupMax = transportationState.pickup.time.to.clone().tz(timeZone);
            const from = dropOffTime.from.clone().tz(timeZone);
            const to = dropOffTime.to.clone().tz(timeZone);
            if (from.isBefore(pickupMax) || to.isBefore(pickupMax)) {
                dispatch(showWarningThunk('Drop off cannot be earlier than pick up.'));
            }
        }
        setTransportation({
            ...transportationState!,
            dropOff: {
                ...transportationState!.dropOff!,
                time: dropOffTime
            }
        });
    };

    const handleSaveCustomPriceChange = (leg: 'pickup' | 'dropOff') => (checked: boolean) => {
        if (!transportationState) return;
        if (transportationState.transportType === 'roundtrip') {
            setTransportation({
                ...transportationState,
                pickup: {
                    ...transportationState.pickup!,
                    saveCustomPrice: checked
                },
                dropOff: {
                    ...transportationState.dropOff!,
                    saveCustomPrice: checked
                }
            });
            return;
        }
        setTransportation({
            ...transportationState,
            [leg]: {
                ...transportationState[leg]!,
                saveCustomPrice: checked
            }
        });
    };

    const handleUseSavedCustomPriceChange =
        (leg: 'pickup' | 'dropOff') => (useSaved: boolean, price: number, customPriceId?: number) => {
            if (!transportationState) return;
            setTransportation({
                ...transportationState,
                [leg]: {
                    ...transportationState[leg]!,
                    useSavedCustomPrice: useSaved,
                    price,
                    customPriceId: customPriceId ?? transportationState[leg]?.customPriceId
                }
            });
        };

    const getOpeningTimeForDate = (date: Moment): Moment | null => {
        if (!marketplace?.schedule?.week) return null;
        const dayOfWeek = date.format('dddd').toLowerCase();
        const businessHours = marketplace.schedule.week[dayOfWeek as keyof typeof marketplace.schedule.week];
        const [hour, minute] = businessHours?.open?.split(':').map(Number) || [8, 0];
        return date
            .clone()
            .tz(marketplace.schedule.timeZone)
            .startOf('day')
            .hour(hour)
            .minute(minute)
            .second(0)
            .millisecond(0);
    };

    return (
        <div className={styles.transportationContainer}>
            <div className={styles.container}>
                <div className={styles.recentlyBookedSection}>
                    <RecentlyBooked
                        clientId={clientId}
                        petId={petId}
                        refreshTrigger={recentlyBookedRefreshKey}
                        onViewAll={() => setShowAppointmentHistoryDrawer(true)}
                        onAppointmentClick={(appointment: AppointmentListItem) => {
                            if (appointment.orderId != null && appointment.bookingId != null) {
                                setShowOrderDetail({ orderId: appointment.orderId, bookingId: appointment.bookingId });
                            }
                        }}
                    />
                </div>

                {!edit && multiDatePeriodFrom && multiDatePeriodTo && (
                    <MultipleDateSelector
                        from={multiDatePeriodFrom}
                        to={multiDatePeriodTo}
                        selectedDates={selectedDates}
                        labelDate="Date"
                        hideTimes
                        isRecurrent={false}
                        frequencyOptions={[]}
                        onChangeFrequencyDuration={noop}
                        onChangeSelectedDates={changeSelectedDatesHandler}
                        onChangePeriod={changeMultiDatePeriodHandler}
                    />
                )}

                <TransportationTypeSelector
                    value={transportationState?.transportType}
                    edit={edit}
                    onChange={value => {
                        const clientAddress = getClientAddress();
                        const requiresPickup = value === 'pickup' || value === 'roundtrip';
                        const requiresDropOff = value === 'dropoff' || value === 'roundtrip';
                        const isRoundtrip = value === 'roundtrip';
                        const isPickupOnly = value === 'pickup';
                        const isDropOffOnly = value === 'dropoff';
                        const bookingDate =
                            transportationState?.pickup?.time?.from || transportationState?.dropOff?.time?.from;
                        const openingTime = bookingDate && marketplace ? getOpeningTimeForDate(bookingDate) : null;
                        let pickupTime: { from: Moment; to: Moment };
                        let dropOffTime: { from: Moment; to: Moment };
                        if (openingTime != null) {
                            if (isRoundtrip) {
                                const ranges = getDefaultTimeRangesForRoundtripFromOpening(openingTime, timeZone);
                                pickupTime = ranges.pickup;
                                dropOffTime = ranges.dropOff;
                            } else if (isDropOffOnly) {
                                pickupTime = getDefaultTimeRangeFromOpening(openingTime, timeZone);
                                dropOffTime = {
                                    from: openingTime.clone().add(3, 'hours'),
                                    to: openingTime.clone().add(4, 'hours')
                                };
                            } else {
                                pickupTime = getDefaultTimeRangeFromOpening(openingTime, timeZone);
                                dropOffTime = getDefaultTimeRangeFromOpening(openingTime, timeZone);
                            }
                        } else {
                            const ranges = isRoundtrip
                                ? getDefaultTimeRangesForRoundtrip(timeZone)
                                : { pickup: getDefaultTimeRange(timeZone), dropOff: getDefaultTimeRange(timeZone) };
                            pickupTime = ranges.pickup;
                            dropOffTime = ranges.dropOff;
                        }

                        const serviceId = transportationState!.service?.serviceId;
                        const pickupCatalog = transportationState!.service?.pickupPrice || 0;
                        const dropOffCatalog = transportationState!.service?.dropoffPrice || 0;
                        const pickupResolved = serviceId
                            ? resolvePickupPrice(pickupCatalog, customPrices || [], serviceId)
                            : { price: pickupCatalog };
                        const dropoffResolved = serviceId
                            ? resolveDropoffPrice(dropOffCatalog, customPrices || [], serviceId)
                            : { price: dropOffCatalog };
                        const activeMembershipItem = membershipItem ?? transportationState?.membershipItem;
                        const activeEnrollment = enrollment ?? transportationState?.enrollment;
                        const applyMembership = activeMembershipItem != null && activeEnrollment != null;

                        const resolveLegDiscount = (catalogPrice: number): ValueTypeSelectorValue | undefined => {
                            if (!applyMembership || !activeMembershipItem) return undefined;
                            return computeMembershipPreviewDiscount(catalogPrice, activeMembershipItem) ?? undefined;
                        };

                        setTransportation({
                            ...transportationState!,
                            transportType: value!,
                            enrollment: activeEnrollment,
                            membershipItem: activeMembershipItem,
                            pickup: {
                                ...transportationState!.pickup!,
                                time: pickupTime,
                                price: pickupResolved.price,
                                customPriceId: pickupResolved.customPriceId,
                                discount: resolveLegDiscount(pickupCatalog),
                                address:
                                    requiresPickup && (transportationState!.pickup?.address?.trim() || clientAddress)
                                        ? transportationState!.pickup?.address?.trim() || clientAddress
                                        : '',
                                instructions: transportationState!.pickup?.instructions || '',
                                saveCustomPrice: false,
                                useSavedCustomPrice: undefined
                            },
                            dropOff: {
                                ...transportationState!.dropOff!,
                                time: dropOffTime,
                                price: dropoffResolved.price,
                                customPriceId: dropoffResolved.customPriceId,
                                discount: resolveLegDiscount(dropOffCatalog),
                                address:
                                    requiresDropOff && (transportationState!.dropOff?.address?.trim() || clientAddress)
                                        ? transportationState!.dropOff?.address?.trim() || clientAddress
                                        : '',
                                instructions: transportationState!.dropOff?.instructions || '',
                                saveCustomPrice: false,
                                useSavedCustomPrice: undefined
                            }
                        });
                    }}
                />

                <TransportationServiceSelector
                    loading={servicesStatus === ServicesStatus.FetchingTransportationServices}
                    transportType={transportationState?.transportType}
                    className={styles.serviceSelector}
                    value={transportationState?.service}
                    onChange={value => {
                        const previousServiceId = transportationState?.service?.serviceId;
                        const serviceChanged = previousServiceId !== value!.serviceId;
                        const keepEditPrices = !serviceChanged;
                        const pickupResolved = resolvePickupPrice(
                            value!.pickupPrice,
                            customPrices || [],
                            value!.serviceId
                        );
                        const dropoffResolved = resolveDropoffPrice(
                            value!.dropoffPrice,
                            customPrices || [],
                            value!.serviceId
                        );
                        const activeMembershipItem = membershipItem ?? transportationState?.membershipItem;
                        const activeEnrollment = enrollment ?? transportationState?.enrollment;
                        const applyMembership = activeMembershipItem != null && activeEnrollment != null;

                        const resolveLegDiscount = (catalogPrice: number): ValueTypeSelectorValue | undefined => {
                            if (!applyMembership || !activeMembershipItem) {
                                return undefined;
                            }
                            return computeMembershipPreviewDiscount(catalogPrice, activeMembershipItem) ?? undefined;
                        };

                        setTransportation({
                            ...transportationState!,
                            service: value!,
                            vehicle: undefined,
                            enrollment: activeEnrollment,
                            membershipItem: activeMembershipItem,
                            pickup: {
                                ...transportationState!.pickup!,
                                price: keepEditPrices
                                    ? transportationState!.pickup?.price ?? pickupResolved.price
                                    : pickupResolved.price,
                                customPriceId: pickupResolved.customPriceId,
                                saveCustomPrice: keepEditPrices ? transportationState!.pickup?.saveCustomPrice : false,
                                useSavedCustomPrice: keepEditPrices
                                    ? transportationState!.pickup?.useSavedCustomPrice
                                    : undefined,
                                discount:
                                    resolveLegDiscount(value!.pickupPrice) ?? transportationState!.pickup?.discount
                            },
                            dropOff: {
                                ...transportationState!.dropOff!,
                                price: keepEditPrices
                                    ? transportationState!.dropOff?.price ?? dropoffResolved.price
                                    : dropoffResolved.price,
                                customPriceId: dropoffResolved.customPriceId,
                                saveCustomPrice: keepEditPrices ? transportationState!.dropOff?.saveCustomPrice : false,
                                useSavedCustomPrice: keepEditPrices
                                    ? transportationState!.dropOff?.useSavedCustomPrice
                                    : undefined,
                                discount:
                                    resolveLegDiscount(value!.dropoffPrice) ?? transportationState!.dropOff?.discount
                            }
                        });
                    }}
                />

                {transportationState?.service && (
                    <StaffSelector
                        loading={!transportationState?.service}
                        type="transportation"
                        key={transportationState?.service?.serviceId || 'no-service'}
                        selectedStaff={transportationState?.selectedStaff?.id || auth.user!.staffId}
                        service={transportationState?.service}
                        onSelectStaff={value =>
                            setTransportation({
                                ...transportationState!,
                                selectedStaff: value!
                            })
                        }
                    />
                )}

                {transportationState?.service && (
                    <TransportationServiceVehicleSelector
                        loading={vehiclesStatus === VehiclesStatus.Fetching}
                        value={transportationState?.vehicle ?? undefined}
                        onChange={value =>
                            setTransportation({
                                ...transportationState!,
                                vehicle: value!
                            })
                        }
                    />
                )}

                {(transportationState?.transportType === 'pickup' ||
                    transportationState?.transportType === 'roundtrip') && (
                    <TransportationSection
                        key={`pickup-${
                            transportationState?.transportType
                        }-${transportationState?.pickup?.time?.from?.valueOf()}-${
                            transportationState?.service?.serviceId ?? 'no-service'
                        }`}
                        title="Pick Up"
                        service={transportationState?.service !== undefined}
                        addressLabel="Pick Up Address"
                        instructionsLabel="Pick Up Instructions"
                        from={transportationState?.pickup?.time?.from}
                        to={transportationState?.pickup?.time?.to}
                        hideDate={!edit}
                        price={transportationState?.pickup?.price}
                        discount={transportationState?.pickup?.discount}
                        address={transportationState?.pickup?.address}
                        instructions={transportationState?.pickup?.instructions}
                        saveCustomPrice={transportationState?.pickup?.saveCustomPrice}
                        useSavedCustomPrice={transportationState?.pickup?.useSavedCustomPrice}
                        petId={petId}
                        serviceId={transportationState?.service?.serviceId}
                        catalogPrice={transportationState?.service?.pickupPrice}
                        customPriceKind="pickup"
                        onTimeChange={handlePickupTimeChange}
                        onPriceChange={value =>
                            setTransportation({
                                ...transportationState!,
                                pickup: {
                                    ...transportationState!.pickup!,
                                    price: value!
                                }
                            })
                        }
                        onDiscountChange={value =>
                            setTransportation({
                                ...transportationState!,
                                pickup: {
                                    ...transportationState!.pickup!,
                                    discount: value!
                                }
                            })
                        }
                        onAddressChange={value =>
                            setTransportation({
                                ...transportationState!,
                                pickup: {
                                    ...transportationState!.pickup!,
                                    address: value!
                                }
                            })
                        }
                        onInstructionsChange={value =>
                            setTransportation({
                                ...transportationState!,
                                pickup: {
                                    ...transportationState!.pickup!,
                                    instructions: value!
                                }
                            })
                        }
                        onSaveCustomPriceChange={handleSaveCustomPriceChange('pickup')}
                        onUseSavedCustomPriceChange={handleUseSavedCustomPriceChange('pickup')}
                    />
                )}

                {(transportationState?.transportType === 'dropoff' ||
                    transportationState?.transportType === 'roundtrip') && (
                    <TransportationSection
                        key={`dropoff-${
                            transportationState?.transportType
                        }-${transportationState?.dropOff?.time?.from?.valueOf()}-${
                            transportationState?.service?.serviceId ?? 'no-service'
                        }`}
                        title="Drop Off"
                        service={transportationState?.service !== undefined}
                        addressLabel="Drop Off Address"
                        instructionsLabel="Drop Off Instructions"
                        from={transportationState?.dropOff?.time?.from}
                        to={transportationState?.dropOff?.time?.to}
                        hideDate={!edit}
                        price={transportationState?.dropOff?.price}
                        discount={transportationState?.dropOff?.discount}
                        address={transportationState?.dropOff?.address}
                        instructions={transportationState?.dropOff?.instructions}
                        saveCustomPrice={transportationState?.dropOff?.saveCustomPrice}
                        useSavedCustomPrice={transportationState?.dropOff?.useSavedCustomPrice}
                        petId={petId}
                        serviceId={transportationState?.service?.serviceId}
                        catalogPrice={transportationState?.service?.dropoffPrice}
                        customPriceKind="dropoff"
                        onTimeChange={handleDropOffTimeChange}
                        onPriceChange={value =>
                            setTransportation({
                                ...transportationState!,
                                dropOff: {
                                    ...transportationState!.dropOff!,
                                    price: value!
                                }
                            })
                        }
                        onDiscountChange={value =>
                            setTransportation({
                                ...transportationState!,
                                dropOff: {
                                    ...transportationState!.dropOff!,
                                    discount: value!
                                }
                            })
                        }
                        onAddressChange={value =>
                            setTransportation({
                                ...transportationState!,
                                dropOff: {
                                    ...transportationState!.dropOff!,
                                    address: value!
                                }
                            })
                        }
                        onInstructionsChange={value =>
                            setTransportation({
                                ...transportationState!,
                                dropOff: {
                                    ...transportationState!.dropOff!,
                                    instructions: value!
                                }
                            })
                        }
                        onSaveCustomPriceChange={handleSaveCustomPriceChange('dropOff')}
                        onUseSavedCustomPriceChange={handleUseSavedCustomPriceChange('dropOff')}
                    />
                )}
            </div>
            <div className={styles.footerContainer}>
                <Button
                    isLoading={
                        newBookingStatus === NewBookingsStatus.SavingCreateAppointment ||
                        newBookingStatus === NewBookingsStatus.SingleRescheduling
                    }
                    className={styles.buttonSave}
                    disabled={isDisabled}
                    variant="green"
                    label="Save"
                    size="large"
                    onClick={() => {
                        const resolvedSelectedDates = edit
                            ? ([
                                  (transportationState.pickup?.time?.from || transportationState.dropOff?.time?.from)
                                      ?.clone()
                                      .tz(tz)
                                      .startOf('day')
                              ].filter(Boolean) as Array<Moment>)
                            : selectedDates.map(d => d.clone().tz(tz).startOf('day'));

                        // Align leg clocks to first selected day so slot builder has consistent clocks.
                        const anchorDay = resolvedSelectedDates[0];
                        let payloadState = transportationState;
                        if (!edit && anchorDay) {
                            const syncLeg = (leg?: typeof transportationState.pickup) =>
                                leg?.time?.from && leg?.time?.to
                                    ? {
                                          ...leg,
                                          time: {
                                              from: mergeTransportationDayWithClock(anchorDay, leg.time.from, tz),
                                              to: mergeTransportationDayWithClock(anchorDay, leg.time.to, tz)
                                          }
                                      }
                                    : leg;
                            payloadState = {
                                ...transportationState,
                                pickup: syncLeg(transportationState.pickup),
                                dropOff: syncLeg(transportationState.dropOff)
                            };
                        }

                        const resolvedEnrollment = enrollment ?? payloadState.enrollment;
                        const resolvedMembershipItem = membershipItem ?? payloadState.membershipItem;
                        const payload: Transportation = {
                            ...payloadState,
                            selectedDates: resolvedSelectedDates
                        };
                        if (resolvedEnrollment != null) {
                            payload.enrollment = resolvedEnrollment;
                        } else {
                            delete (payload as { enrollment?: unknown }).enrollment;
                        }
                        if (resolvedMembershipItem != null) {
                            payload.membershipItem = resolvedMembershipItem;
                        } else {
                            delete (payload as { membershipItem?: unknown }).membershipItem;
                        }
                        onNext(payload);
                    }}
                />
            </div>
            <AppointmentHistoryDrawer
                open={showAppointmentHistoryDrawer}
                onBack={() => {
                    setShowAppointmentHistoryDrawer(false);
                    setRecentlyBookedRefreshKey(k => k + 1);
                }}
                customerId={clientId}
                petId={petId}
                onAppointmentClick={(appointment: AppointmentListItem) => {
                    if (appointment.orderId != null && appointment.bookingId != null) {
                        setShowOrderDetail({ orderId: appointment.orderId, bookingId: appointment.bookingId });
                    }
                }}
            />
            {showOrderDetail && (
                <OrderDetail
                    orderId={showOrderDetail.orderId}
                    bookingId={showOrderDetail.bookingId}
                    readOnly={true}
                    onClose={() => setShowOrderDetail(null)}
                    zIndex={10100}
                />
            )}
        </div>
    );
};

export default TransportationComponent;
